import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { dramas } from "@/mocks/dramas";

interface SearchResult {
  key: string;
  dramaId: string;
  title: string;
  subtitle: string;
  image: string;
  type: "剧集" | "角色";
}

export default function SearchBox() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const results = useMemo<SearchResult[]>(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    const list: SearchResult[] = [];
    dramas.forEach((drama) => {
      if (drama.title.toLowerCase().includes(keyword)) {
        list.push({
          key: `d-${drama.id}`,
          dramaId: drama.id,
          title: drama.title,
          subtitle: `${drama.year} · ${drama.category}`,
          image: drama.poster,
          type: "剧集",
        });
      }
      drama.characters.forEach((character) => {
        if (
          character.name.toLowerCase().includes(keyword) ||
          character.actor.toLowerCase().includes(keyword)
        ) {
          list.push({
            key: `c-${character.id}`,
            dramaId: drama.id,
            title: character.name,
            subtitle: `${drama.title} · ${character.actor} 饰`,
            image: character.image,
            type: "角色",
          });
        }
      });
    });
    return list.slice(0, 6);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goDetail = (dramaId: string) => {
    setQuery("");
    setOpen(false);
    navigate(`/detail/${dramaId}`);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    setOpen(false);
    setQuery("");
    navigate(`/category?q=${encodeURIComponent(keyword)}`);
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <form onSubmit={handleSubmit} className="relative" role="search">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          type="search"
          name="q"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索剧集或角色"
          aria-label="搜索剧集或角色"
          className="w-full h-10 md:h-11 pl-10 pr-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-500 focus:bg-background-50"
        />
      </form>

      {open && query.trim() && (
        <div className="absolute left-0 right-0 top-full mt-2 rounded-lg border border-background-200 bg-background-50 shadow-lg overflow-hidden z-50">
          {results.length > 0 ? (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((result) => (
                <li key={result.key}>
                  <button
                    type="button"
                    onClick={() => goDetail(result.dramaId)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-primary-50 transition-colors"
                  >
                    <span className="w-10 h-12 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={result.image}
                        alt={result.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground-900 truncate">
                          {result.title}
                        </span>
                        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800">
                          {result.type}
                        </span>
                      </span>
                      <span className="block text-xs text-foreground-500 truncate mt-0.5">
                        {result.subtitle}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center">
              <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
              <p className="mt-2 text-sm text-foreground-600">
                没有找到与「{query.trim()}」相关的剧集或角色
              </p>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/category");
                }}
                className="mt-3 text-xs text-primary-700 hover:text-primary-800 whitespace-nowrap"
              >
                浏览全部剧集
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}